import React, { useEffect } from "react";
import Section from "../sections/Section";
import arePropsEqual from "../../utils/arePropsEqual";
import { buildCollapseStylesheet } from "./ElementCollapseStyleResolver";
import BuilderStyleTag from "../shared/BuilderStyleTag";

const ElementCollapse = (props) => {
  let element = props.element;
  const classes = style.classes(element, props.builder, props.device);
  const stylesheet = buildCollapseStylesheet(
    props.builder,
    props.device,
    props.cta,
    element,
    props.variant
  );

  let sorted_list_items = Object.values(element.content_toggle_items)
    .filter(
      (list_item) =>
        list_item.element_id == element.id && list_item.toBeDeleted !== true
    )
    .sort((a, b) => a.position - b.position);

  const toggleCollapsePosition = (position) => {
    let collapsed_position = position;
    if (element.options["collapsed-position"] == collapsed_position) {
      collapsed_position = null;
    }

    let updates = [
      {
        object_type: "elements",
        object_id: element.id,
        setting_name: "[options][collapsed-position]",
        value: collapsed_position,
        skip_history: false,
      },
    ];

    dispatchCustomEvent("updateBuilder", { updates: updates });
  }

  const handleCollapseHeadingContextMenu = (e) => {
    e.preventDefault();
    document.dispatchEvent(
      new CustomEvent("showPopover", {
        detail: {
          object_type: "elements",
          object_id: element.id,
          tab: "Content",
          object: element,
          anchorPosition: { x: e.clientX, y: e.clientY },
        },
      })
    );
  };

  useEffect(() => {
    document.addEventListener(`cf_element_${element.id}_select_toggle_item`, (e) => {
      toggleCollapsePosition(e.detail.toggle_item.position);
    });

    return () => {
      document.removeEventListener(`cf_element_${element.id}_select_toggle_item`, (e) => {
        toggleCollapsePosition(e.detail.toggle_item.position);
      });
    };
  }, []);

  return (
    <React.Fragment>
      <div className="element-content">
        <div
          className={classes}
          id={`cf-collapse${element.id}`}
          data-font={
            element.options["collapse-heading-font-family"] ||
            props.variant.options["collapse-heading-font-family"]
          }
          role="tablist"
        >
          {sorted_list_items.map((list_item) => {
            let collapse_item_section = Object.values(element.sections).filter(
              (section) =>
                section.content_toggle_item_id == list_item.id &&
                section.step_id == props.section.step_id
            )[0];

            let list_item_collapsed = element.options["collapsed-position"]
              ? element.options["collapsed-position"] == list_item.position
              : null;

            return (
              <React.Fragment key={list_item.id}>
                <div
                  className="cf-collapse"
                  data-collapse-position={list_item.position}
                  role="tab"
                  tabIndex="0"
                  aria-controls={`cf-collapse-item-${list_item.id}`}
                  aria-expanded={!list_item_collapsed}
                  onClick={props.builder ? (e) => {
                    // Only select if not clicking the toggle itself or content inside the collapse box
                    if (
                      e.target.classList.contains('cf-collapse-toggle') ||
                      e.target.closest('.cf-collapse-toggle') ||
                      e.target.classList.contains('cf-collapse-box') ||
                      e.target.closest('.cf-collapse-box')
                    ) {
                      return;
                    }
                    window.dispatchCustomEvent('selectObject', {
                      object_type: 'elements',
                      object_id: element.id,
                      tab: 'Content',
                    });
                  } : undefined}
                >
                  <div
                    id={`cf-collapse-item-heading-${list_item.id}`}
                    className={`cf-collapse-heading ${
                      list_item_collapsed ? "cf-collapse-box-in" : ""
                    }`}
                    onContextMenu={handleCollapseHeadingContextMenu}
                  >
                    <div className="cf-collapse-title" onClick={() => toggleCollapsePosition(list_item.position)}>
                      <div
                        className={`cf-merge-tag-allowed cf-collapse-toggle ${
                          list_item_collapsed ? "cf-collapsed" : ""
                        }`}
                        data-parent={`#cf-collapse${element.id}`}
                        data-target={`#cf-collapse-item-${list_item.id}`}
                        data-list-item-position={list_item.position}
                        data-list-item-element-id={list_item.element_id}
                        dangerouslySetInnerHTML={{
                          __html: list_item.content,
                        }}
                      />
                    </div>
                  </div>

                  <div
                    id={`cf-collapse-item-${list_item.id}`}
                    className={`cf-collapse-box ${
                      list_item_collapsed ? "cf-collapse-box-in" : ""
                    }`}
                    data-collapse-item-id={list_item.id}
                    data-list-item-element-id={list_item.element_id}
                    role="tabpanel"
                    aria-labelledby={`cf-collapse-item-heading-${list_item.id}`}
                    aria-hidden={list_item_collapsed}
                  >
                    <div className="cf-collapse-body">
                      {collapse_item_section ? (
                        <Section
                          key={`collapse-list-item-${list_item.id}`}
                          website={props.website}
                          cta={props.cta}
                          variant={props.variant}
                          builder={props.builder}
                          preview={props.preview}
                          step={props.step}
                          section={collapse_item_section}
                          device={props.device}
                        ></Section>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          <BuilderStyleTag
            builder={props.builder}
            styleKey={`cta:${props.cta.id}:element:${element.id}:collapse`}
            stylesheet={stylesheet}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

const style = {
  classes: (element, builder, device) => {
    let options = element.options;
    let classes = "cf-collapse-element ";
    let alignmentClass = "cf-outer-center";

    if (device == "mobile") {
      if (options["cf-alignment-mobile"]) {
        if (options["cf-alignment-mobile"].indexOf("left") > -1) {
          alignmentClass = "cf-outer-left";
        } else if (options["cf-alignment-mobile"].indexOf("center") > -1) {
          alignmentClass = "cf-outer-center";
        } else if (options["cf-alignment-mobile"].indexOf("right") > -1) {
          alignmentClass = "cf-outer-right";
        }
      }
    } else {
      if (options["cf-alignment"]) {
        if (options["cf-alignment"].indexOf("left") > -1) {
          alignmentClass = "cf-outer-left";
        } else if (options["cf-alignment"].indexOf("center") > -1) {
          alignmentClass = "cf-outer-center";
        } else if (options["cf-alignment"].indexOf("right") > -1) {
          alignmentClass = "cf-outer-right";
        }
      }
    }

    classes += " " + alignmentClass;

    return classes;
  },
};

export default React.memo(ElementCollapse, arePropsEqual);
